import React from "react";
import { DataDisplayRecord } from "components/data-display";
import { GridLayout } from "components/grid";
import { StatusBadge } from "components/status";
import { Intent } from "@blueprintjs/core";
import { ShowHide } from "components/layout";
import {
    ApproveVehicleRegistrationPanel,
    IncompleteVehicleRegistrationPanel,
} from "pages/licence-checking/grey-fleet/check/panel";
import PropTypes from "prop-types";

const defaultArray = [];

export function GreyFleetCheckVehicleOverview({
    isLoading,
    checkData,
    evaluateStatus,
    refreshCheck,
    licenceCheckingOptions,
    coverTypes = defaultArray,
}) {
    const getVehicleRecordProps = () => {
        if (
            Array.isArray(checkData?.missingPolicyInfo) &&
            checkData.missingPolicyInfo.length > 0
        ) {
            return {
                text: "Incomplete record",
                iconName: "error",
                intent: Intent.WARNING,
            };
        } else if (checkData?.asset?.result !== null) {
            return {
                text: "Valid",
                iconName: "tick-circle",
                intent: Intent.SUCCESS,
            };
        } else {
            return {
                text: checkData?.asset?.status,
                iconName: "refresh",
                intent: Intent.PRIMARY,
            };
        }
    };

    const getPolicyStatusData = () => {
        // When the check was created with the policy check ignored, the
        // backend auto-passes the policy step, so the badge reads "Not
        // required" with a tooltip explaining the stage was bypassed. The
        // guard on the completed/passed state matters because a check with
        // the ignore flag set can still have a failed policy step when the
        // vehicle was not found — that case must keep the standard failure
        // rendering.
        if (
            checkData?.ignorePolicyCheck === true &&
            checkData?.asset?.statusKey === "COMPLETED" &&
            checkData?.asset?.result === true
        ) {
            return (
                <StatusBadge
                    text="Not required"
                    iconName="info-sign"
                    intent={Intent.SUCCESS}
                    showTooltip
                    tooltipContent="This vehicle was marked as not required to comply with the company policy, so the policy check was not assessed."
                    tooltipPosition="left"
                />
            );
        } else {
            return evaluateStatus(
                checkData?.asset?.status,
                checkData?.asset?.statusKey,
                checkData?.asset?.result,
                ["POLICYFAILURE", "VEHICLENOTFOUND"]
            );
        }
    };

    return (
        <div className="spacer-bottom">
            <h2>Vehicle Check</h2>
            <GridLayout numberOfColumns={4} gap={30}>
                <DataDisplayRecord
                    header="Vehicle Record"
                    data={<StatusBadge {...getVehicleRecordProps()} />}
                    loading={isLoading}
                />
                <DataDisplayRecord
                    header="Policy Status"
                    data={getPolicyStatusData()}
                    loading={isLoading}
                />
                <DataDisplayRecord
                    header="Tax Status"
                    data={evaluateStatus(
                        checkData?.tax?.status,
                        checkData?.tax?.statusKey,
                        checkData?.tax?.result,
                        "TAXFAILURE"
                    )}
                    loading={isLoading}
                />
                <DataDisplayRecord
                    header="MOT Status"
                    data={evaluateStatus(
                        checkData?.mot?.status,
                        checkData?.mot?.statusKey,
                        checkData?.mot?.result,
                        "MOTFAILURE"
                    )}
                    loading={isLoading}
                />
            </GridLayout>
            <ShowHide
                evaluator={!isLoading && checkData?.enrolled === null}
                show={
                    <ApproveVehicleRegistrationPanel
                        registration={checkData.registration || ""}
                        checkId={checkData.id || 0}
                        refreshCheck={refreshCheck}
                        insurance={checkData?.insurance}
                        coverTypes={coverTypes}
                    />
                }
            />
            <ShowHide
                evaluator={
                    !isLoading &&
                    checkData.asset.statusKey === "AWAITINGRESPONSE" &&
                    checkData.missingPolicyInfo?.length > 0
                }
                show={
                    <IncompleteVehicleRegistrationPanel
                        registration={checkData.registration || ""}
                        checkId={checkData.id || 0}
                        missingPolicyInfo={checkData.missingPolicyInfo || []}
                        policyRules={
                            licenceCheckingOptions.fleetCheckPolicyRules || []
                        }
                        reloadData={refreshCheck}
                    />
                }
            />
        </div>
    );
}

GreyFleetCheckVehicleOverview.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    checkData: PropTypes.object.isRequired,
    evaluateStatus: PropTypes.func.isRequired,
    refreshCheck: PropTypes.func.isRequired,
    licenceCheckingOptions: PropTypes.object.isRequired,
    coverTypes: PropTypes.array,
};
